/* global publish, PUB_SUB_EVENTS */

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.minOrderAmount = this.getAttribute('data-min-order');

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll(
      '.cart-notification__close, .cart-notification__links button[type="button"]'
    ).forEach((closeButton) => closeButton.addEventListener('click', this.close.bind(this)));

    this.notification.addEventListener('click', this.handleQuantityClick.bind(this));
    this.parseState = {};
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      (evt) => {
        if (evt.target !== this.notification || evt.propertyName !== 'transform') return;
        this.notification.focus();
        // eslint-disable-next-line no-undef
        trapFocus(this.notification);
        document.dispatchEvent(new CustomEvent('cart-notification:opened'));
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.updateMinusButtonState();
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    // eslint-disable-next-line no-undef
    removeTrapFocus(this.activeElement);
  }

  async renderContents(parsedState) {
    this.parseState = parsedState;
    this.cartItemKey = parsedState.key || parsedState.items?.[0]?.key;
    this.resetCartTotalItemsCount();
    const resolvedQty = this.resolveNotificationDisplayedQuantity(parsedState);
    if (resolvedQty) this.notificationDisplayedQuantity = resolvedQty;

    this.getSectionsToRender().forEach((section) => {
      const el = document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];
      if (!el || !sectionHtml) return;
      el.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (section.id === 'cart-notification-subtotal') {
        el.classList.toggle('hidden', !el.innerHTML.trim());
      }
    });

    this.syncNotificationDisplayedQuantityFromDom();

    if (this.header) this.header.reveal();
    this.hideUpsells();

    try {
      const cartData = await this.fetchCartData();
      this.updateFreeShippingBar(cartData, this.minOrderAmount);
      this.updateCartTotalItemsLink(cartData.item_count);
      this.updateCartSubtotal(cartData);
    } catch {
      // Cart fetch failed; notification still opens with section HTML.
    }

    this.open();
    this.refreshRelatedUpsells(parsedState.product_id);
  }

  hideUpsells() {
    const upsells = this.querySelector('#cart-notification-upsells');
    if (upsells) upsells.classList.add('hidden');
  }

  showUpsells() {
    const upsells = this.querySelector('#cart-notification-upsells');
    if (upsells) upsells.classList.remove('hidden');
  }

  async refreshRelatedUpsells(productId) {
    const currentUpsells = this.querySelector('#cart-notification-upsells');
    if (!currentUpsells) return;

    this.hideUpsells();

    if (!productId) {
      this.showUpsells();
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const recommendationsUrl = `${root}recommendations/products?section_id=cart-notification-related-products&product_id=${productId}&limit=12`;

    try {
      const response = await fetch(recommendationsUrl, { credentials: 'same-origin' });
      if (!response.ok) {
        this.showUpsells();
        return;
      }

      const html = await response.text();
      const sourceUpsells = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('#cart-notification-upsells');

      if (!sourceUpsells || !sourceUpsells.innerHTML.trim()) {
        this.showUpsells();
        return;
      }

      currentUpsells.replaceWith(sourceUpsells);
      this.showUpsells();
      document.dispatchEvent(new CustomEvent('cart-notification:opened'));
    } catch (e) {
      console.error(e);
      this.showUpsells();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-notification-subtotal',
      },
    ];
  }

  async refreshCartNotificationSubtotal() {
    const host = document.getElementById('cart-notification-subtotal');
    if (!host) return;
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}?sections=cart-notification-subtotal`);
      if (!response.ok) return;
      const data = await response.json();
      const sectionHtml = data['cart-notification-subtotal'];
      if (!sectionHtml) return;
      const inner = this.getSectionInnerHTML(sectionHtml);
      host.innerHTML = inner || '';
      host.classList.toggle('hidden', !inner);
    } catch (e) {
      console.error(e);
    }
  }

  updateCartSubtotal(cartData) {
    const host = document.getElementById('cart-notification-subtotal');
    if (!host) return;

    const itemCount = Number(cartData?.item_count) || 0;
    if (itemCount === 0) {
      host.innerHTML = '';
      host.classList.add('hidden');
      return;
    }

    host.classList.remove('hidden');
    const valueEl = host.querySelector('[data-cart-subtotal-value]');
    const totalCents = Number(cartData?.items_subtotal_price ?? cartData?.total_price) || 0;

    if (valueEl) {
      valueEl.textContent = this.constructor.formatCurrency(totalCents, cartData?.currency);
      return;
    }

    const labelText = host.querySelector('.cart-notification__subtotal-label')?.textContent?.trim() || '';
    host.innerHTML = `
      <div class="cart-notification__subtotal-inner totals" role="status">
        <h2 class="cart-notification__subtotal-label totals__subtotal">${labelText}</h2>
        <p class="cart-notification__subtotal-value totals__subtotal-value" data-cart-subtotal-value>
          ${this.constructor.formatCurrency(totalCents, cartData?.currency)}
        </p>
      </div>
    `;
  }

  // eslint-disable-next-line class-methods-use-this
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : '';
  }

  handleBodyClick(evt) {
    const { target } = evt;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // eslint-disable-next-line class-methods-use-this
  async fetchCartData() {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error(`Network response was not ok ${response.statusText}`);
    }
    return response.json();
  }

  handleQuantityClick(evt) {
    const { target } = evt;
    if (target.matches('.plus-icon') || target.matches('.icon-minus')) {
      const quantityInput = target.closest('.quantity');
      const input = quantityInput.querySelector('.quantity__input');

      let newQuantity = parseInt(input.value, 10);
      newQuantity = newQuantity < 1 ? 1 : newQuantity;
      input.value = newQuantity;
      this.notificationDisplayedQuantity = newQuantity;
      this.updateCartQuantity(input.id, newQuantity, target);
      const qtyBox = document.querySelector('.quantity-container .quantity');
      qtyBox.style.pointerEvents = 'none';
    }

    const buttonElement = target.closest('.cart-remove-button-notification');
    if (buttonElement) {
      evt.preventDefault();
      const variantId = buttonElement.getAttribute('data-variant-id');
      this.removeCartItem(variantId);
    }
  }

  async removeCartItem(variantId) {
    const updates = {};
    updates[variantId] = 0;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok ${response.statusText}`);
      }

      const data = await response.json();
      if (data) {
        const cartData = await this.fetchCartData();
        const cartItemCount = cartData.item_count;

        const cartCountSpan = document.querySelector('.cart-count-bubble');
        if (cartCountSpan) {
          cartCountSpan.innerHTML = `<span aria-hidden="true">&nbsp;(${cartItemCount})</span><span class="visually-hidden">&nbsp;${cartItemCount} items</span>`;
        }
        this.setCartNotificationViewButtonText(cartItemCount);
        this.updateCartTotalItemsLink(cartItemCount);

        this.updateFreeShippingBar(cartData, this.minOrderAmount);
        this.updateCartSubtotal(cartData);
        await this.refreshCartNotificationSubtotal();

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-notification',
          cartData,
          variantId,
        });

        this.close();
      }
    } catch (error) {
      console.error('Failed to update cart quantity:', error);
    }
  }

  async updateCartQuantity(variantId, quantity, target) {
    const updates = {};
    const variantItemId = variantId.replace('Quantity-', '');
    updates[variantItemId] = quantity;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok ${response.statusText}`);
      }

      const data = await response.json();
      if (data) {
        const cartData = await this.fetchCartData();
        const cartItem = cartData.items.find((item) => item.id === parseInt(variantItemId, 10));

        if (cartItem) {
          const itemPrice = cartItem.original_line_price;
          const cartItemCount = cartData.item_count;
          const { currency } = cartData;

          const cartCountSpan = document.querySelector('.cart-count-bubble');
          if (cartCountSpan) {
            cartCountSpan.innerHTML = `<span aria-hidden="true">&nbsp;(${cartItemCount})</span><span class="visually-hidden">&nbsp;${cartItemCount} items</span>`;
          }
          this.setCartNotificationViewButtonText(cartItemCount);
          this.updateCartTotalItemsLink(cartItemCount);
          document.querySelector('.cart-notification-product__info-subtotal .product-item-price').innerHTML =
            this.formatPrice(itemPrice, currency);

          const notificationMsgElement = document.querySelector('.item_notification_msg');
          if (target.matches('.plus-icon')) {
            notificationMsgElement.innerHTML = '1 Item added to bag';
          } else {
            notificationMsgElement.innerHTML = '1 Item removed from bag';
          }

          const qtyBoxUpdated = document.querySelector('.quantity-container .quantity');
          qtyBoxUpdated.style.pointerEvents = 'auto';
          this.updateMinusButtonState();

          this.updateFreeShippingBar(cartData, this.minOrderAmount);
          this.updateCartSubtotal(cartData);
          await this.refreshCartNotificationSubtotal();
        } else {
          console.warn(`Cart item with ID ${variantItemId} not found`);
        }
      }
    } catch (error) {
      console.error('Failed to update cart quantity:', error);
    }
  }

  resetCartTotalItemsCount() {
    const countEl = this.querySelector('.cart-notification__total-items-count');
    if (!countEl) return;
    countEl.textContent = '';
    countEl.classList.add('hidden');
    countEl.setAttribute('aria-hidden', 'true');
  }

  resolveNotificationDisplayedQuantity(parsedState) {
    const key = parsedState.key || this.cartItemKey;
    if (parsedState.items?.length && key) {
      const line = parsedState.items.find((item) => item.key === key);
      if (line?.quantity != null) return Number(line.quantity) || 1;
    }
    if (parsedState.quantity != null) return Number(parsedState.quantity) || 1;
    return null;
  }

  syncNotificationDisplayedQuantityFromDom() {
    const quantityInputs = this.querySelectorAll('#cart-notification-product .quantity__input');
    if (quantityInputs.length) {
      const total = Array.from(quantityInputs).reduce(
        (sum, input) => sum + (parseInt(input.value, 10) || 0),
        0
      );
      if (total > 0) {
        this.notificationDisplayedQuantity = total;
      }
    }
  }

  getNotificationDisplayedQuantity() {
    if (Number.isFinite(this.notificationDisplayedQuantity) && this.notificationDisplayedQuantity > 0) {
      return this.notificationDisplayedQuantity;
    }

    const quantityInputs = this.querySelectorAll('#cart-notification-product .quantity__input');
    if (quantityInputs.length) {
      return Array.from(quantityInputs).reduce(
        (sum, input) => sum + (parseInt(input.value, 10) || 0),
        0
      );
    }

    return 1;
  }

  getMoreItemsCount(cartItemCount) {
    const totalCount = Number(cartItemCount) || 0;
    const displayedCount = this.getNotificationDisplayedQuantity();
    return Math.max(0, totalCount - displayedCount);
  }

  updateCartTotalItemsLink(cartItemCount) {
    const link = this.querySelector('#cart-notification-total-items-link');
    const totalContainer = this.querySelector('#cart-notification-total');
    const moreItemsCount = this.getMoreItemsCount(cartItemCount);

    if (totalContainer) {
      totalContainer.classList.toggle('hidden', moreItemsCount === 0);
    }

    if (!link) return;

    const textEl = link.querySelector('.cart-notification__total-items-text');
    const prefixEl = link.querySelector('.cart-notification__total-items-prefix');
    const countEl = link.querySelector('.cart-notification__total-items-count');
    const suffixEl = link.querySelector('.cart-notification__total-items-suffix');
    const totalTemplate =
      moreItemsCount === 1 ? this.dataset.i18nTotalItemOne : this.dataset.i18nTotalItemsOther;
    const viewTemplate = this.dataset.i18nViewBag;
    const countStr = String(moreItemsCount);

    if (totalTemplate && prefixEl && countEl && suffixEl) {
      if (totalTemplate.includes('__COUNT__')) {
        const templateParts = totalTemplate.split('__COUNT__');
        prefixEl.textContent = templateParts[0] || '';
        suffixEl.textContent = templateParts[1] || '';
        countEl.textContent = countStr;
        countEl.classList.remove('hidden');
        countEl.removeAttribute('aria-hidden');
      } else if (textEl) {
        textEl.textContent = totalTemplate.replace(/\d+/, countStr);
      }
    } else if (textEl && totalTemplate) {
      textEl.textContent = totalTemplate.split('__COUNT__').join(countStr);
    }

    if (viewTemplate) {
      link.setAttribute('aria-label', viewTemplate.split('__COUNT__').join(String(cartItemCount)));
    }
  }

  setCartNotificationViewButtonText(itemCount) {
    const buttonEl = document.querySelector('#cart-notification-button');
    if (!buttonEl) return;
    const viewTemplate = this.dataset.i18nViewBag;
    if (!viewTemplate) return;
    buttonEl.textContent = viewTemplate.split('__COUNT__').join(String(itemCount));
  }

  updateMinusButtonState() {
    const quantityInput = this.notification.querySelector('.quantity__input');
    const minusButton = this.notification.querySelector('.icon-minus');
    if (quantityInput && minusButton) {
      const quantity = parseInt(quantityInput.value, 10);
      minusButton.disabled = quantity <= 1;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  formatPrice(price, currency) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price / 100);
  }

  static parseMinOrderCents(targetVal) {
    const raw = String(targetVal ?? '').trim();
    if (!raw) return 0;
    const normalized = raw.replace(/[^\d.,]/g, '');
    if (!normalized) return 0;
    const numberString =
      normalized.includes(',') && !normalized.includes('.')
        ? normalized.replace(',', '.')
        : normalized.replace(/,/g, '');
    const value = Number(numberString);
    if (!Number.isFinite(value) || value <= 0) return 0;
    return Math.round(value * 100);
  }

  static formatCurrency(cents, currency) {
    const amount = (Number(cents) || 0) / 100;
    const locale = document.documentElement.lang || navigator.language || 'en-US';
    const code = currency || 'USD';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  updateFreeShippingBar(cartData, targetVal) {
    const root = this.querySelector('[data-free-shipping-bar]') || this;
    const progressBarContainer = root.matches?.('.free-shipping__wrapper')
      ? root
      : root.querySelector('.free-shipping__wrapper');
    const progressBar = root.querySelector('.free-shippingThreshold__progress');
    const progressNum =
      root.querySelector('[data-free-shipping-amount]') || root.querySelector('.free-shipping__num');
    const successMsg = root.querySelector('.free-shipping__success-msg');
    const remainingMsg = root.querySelector('.free-shipping__remaining-msg');

    if (!progressBarContainer || !progressBar || !progressNum || !successMsg || !remainingMsg) return;

    const minOrderCents = this.constructor.parseMinOrderCents(targetVal);
    if (!minOrderCents) {
      progressBarContainer.classList.add('hidden');
      return;
    }

    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    const totalCents = Number(cartData?.total_price) || 0;

    let freeShipItemsCount = 0;
    const freeShipCents = items.reduce((acc, item) => {
      const hasFreeShipping = !!(item?.properties && item.properties.freeShipping);
      if (hasFreeShipping) {
        freeShipItemsCount += 1;
        return acc + (Number(item.final_line_price) || 0);
      }
      return acc;
    }, 0);

    if (items.length > 0 && freeShipItemsCount === items.length) {
      progressBarContainer.classList.add('hidden');
      return;
    }

    progressBarContainer.classList.remove('hidden');

    const eligibleTotalCents = Math.max(0, totalCents - freeShipCents);
    const remainingCents = Math.max(0, minOrderCents - eligibleTotalCents);
    const progress = Math.min(100, Math.round((eligibleTotalCents / minOrderCents) * 100));

    progressBar.style.width = `${Number.isFinite(progress) ? progress : 0}%`;

    const reached = remainingCents === 0;
    successMsg.hidden = !reached;
    remainingMsg.hidden = reached;

    progressNum.textContent = this.constructor.formatCurrency(remainingCents, cartData?.currency);
  }
}

customElements.define('cart-notification', CartNotification);
